import { useTheme } from '@emotion/react';
import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useMemo } from 'react';

import { DiffCompareColumns } from '~/ui/domain/chat/DiffCompare';

import { htmlDiffer } from '../../../app/common/html-differ';
import type { Diff } from '../../../data-contract/chat';
import { diffSupportsCompare, isEntityDiff, isSkillChangesDiff, textValue } from './diff-helpers';
import { DiffEntitiesView, SkillChangesView } from './EntityDiffView';

// Tailwind preflight is disabled (to avoid clashing with MUI), so we reset and
// re-apply margins/markers on agent-emitted p/ul/ol by hand.
const htmlChildStyles = [
    '[&_p]:mt-0 [&_p]:mb-s [&_p:last-child]:mb-0',
    '[&_ul]:mt-0 [&_ul]:mb-s [&_ul:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-l',
    '[&_ol]:mt-0 [&_ol]:mb-s [&_ol:last-child]:mb-0 [&_ol]:list-decimal [&_ol]:pl-l',
].join(' ');

const HtmlContent: FunctionComponent<{ readonly html: string }> = ({ html }) => (
    <div
        className={`wrap-break-word text-sm leading-snug text-black-800 ${htmlChildStyles}`}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Change-proposal diff values are rich HTML produced by the agent backend.
        dangerouslySetInnerHTML={{ __html: html }}
    />
);

const HtmlDiffContent: FunctionComponent<{
    readonly compareAgainst: string;
    readonly current: string;
    readonly type: 'added' | 'removed';
}> = ({ compareAgainst, current, type }) => {
    const theme = useTheme();
    const diff = useMemo(
        () =>
            htmlDiffer({
                compareAgainst,
                current,
                theme,
                type,
            }),
        [compareAgainst, current, theme, type],
    );

    return <HtmlContent html={diff} />;
};

const DiffPlainText: FunctionComponent<{ readonly oldText: string; readonly newText: string }> = ({
    oldText,
    newText,
}) => (
    <div className="flex flex-col gap-xs">
        <div className="wrap-break-word text-sm leading-snug tracking-tight text-gray-600 line-through">{oldText}</div>
        <div className="wrap-break-word text-lg font-medium leading-snug tracking-tight text-black-800">{newText}</div>
    </div>
);

export const DiffBody: FunctionComponent<{ readonly diff: Diff; readonly compareActive: boolean }> = ({
    diff,
    compareActive,
}) => {
    if (isSkillChangesDiff(diff)) {
        return <SkillChangesView diff={diff} />;
    }
    if (isEntityDiff(diff)) {
        return <DiffEntitiesView diff={diff} />;
    }
    const oldText = textValue(diff.oldValue);
    const newText = textValue(diff.newValue);

    if (!diffSupportsCompare(diff)) {
        return <DiffPlainText oldText={oldText} newText={newText} />;
    }

    if (compareActive) {
        const hasCurrent = is.nonEmptyString(oldText);
        return (
            <DiffCompareColumns
                currentEmpty={!hasCurrent}
                current={
                    hasCurrent ? (
                        <HtmlDiffContent compareAgainst={oldText} current={newText} type="removed" />
                    ) : (
                        'No current value.'
                    )
                }
                proposed={<HtmlDiffContent compareAgainst={oldText} current={newText} type="added" />}
            />
        );
    }

    return <HtmlContent html={newText} />;
};
